/** @jsx jsx */
import { jsx } from "@emotion/react";
import { useCallback, useState, useEffect } from "react";
import { useResizeDetector } from "react-resize-detector";
import { graphql } from "gatsby";
import parse from "html-react-parser";
import App from "../components/App";
import Head from "../components/Head";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Canvas from "../components/Canvas";
import VisuallyHidden from "../components/VisuallyHidden";
import DynamicSvg from "../components/DynamicSvg";
import PropsPost from "../types/PropsPost";
import siteConfig from "../../data/siteConfig";

const Post = (props: PropsPost) => {
  // destructure props
  const { allMarkdownRemark, markdownRemark } = props.data;
  const { html, frontmatter } = markdownRemark;
  const {
    slug,
    title,
    titleLang,
    author,
    color,
    params,
    initialPoints,
    norms,
  } = frontmatter;

  const data = {
    title: title + " | " + siteConfig.title,
    description:
      slug === "editors"
        ? "Nexo organizers Lois Armas and Noora Pitkälä write about Nexo meetings and the experience of constructing a community of writers online."
        : `By ${author} for Nexo Zine, a compilation of texts that makes the brave process of sharing our writing visible.`,
    image: `/png/${slug}.png`,
    url: siteConfig.url + slug,
    author: author,
  };

  // initialize state for the viewport width and content height
  const [viewportWidth, setViewportWidth] = useState(1);
  const [contentHeight, setContentHeight] = useState(1);

  // follow the size of the viewport and the content
  const resizeDetector = useResizeDetector({
    onResize: useCallback(() => {
      setViewportWidth(window.innerWidth);
    }, []),
  });

  useEffect(() => {
    setContentHeight(resizeDetector.height || 1);
  });

  // get the index of the current post within the posts array
  const current = allMarkdownRemark.edges.findIndex(
    (edge) => edge.node.frontmatter.slug === slug
  );

  // get prev and next indices (or null as applicable)
  const prev =
    current < 1 ? null : allMarkdownRemark.edges[current - 1].node.frontmatter;
  const next =
    current > allMarkdownRemark.edges.length - 2
      ? null
      : allMarkdownRemark.edges[current + 1].node.frontmatter;

  return (
    <App>
      <Head data={data} />
      <div
        css={{
          // display as grid
          display: "grid",
          // change grid columns and rows depending on viewport width
          /* stylelint-disable */
          gridTemplateColumns: viewportWidth < 800 ? "1fr" : `2fr 1fr`,
          gridTemplateRows:
            viewportWidth < 800 ? `${viewportWidth}px auto` : "auto",
          /* styleling-enable */
        }}
      >
        <div
          css={{
            gridArea: viewportWidth < 800 ? "2 / 1 / 3 / 2" : "1 / 1 / 2 / 2",
          }}
        >
          <div
            ref={resizeDetector.ref}
            css={{
              minHeight: "100vh",
              paddingRight: viewportWidth < 800 ? "1.5rem" : "3rem",
              paddingLeft: viewportWidth < 800 ? "1.5rem" : "3rem",
              "h1, h2, h3, h4, h5, h6": {
                color: color,
              },
            }}
          >
            <header
              css={{ paddingTop: viewportWidth < 800 ? "1.5rem" : "3rem" }}
            >
              <VisuallyHidden space="overlay" css={{ top: "-1em" }}>
                <a href="#main">Skip to main content</a>
              </VisuallyHidden>
              <Nav prev={prev} next={next} color={color} />
            </header>
            <main
              id="main"
              css={{
                borderColor: color,
                borderStyle: "solid",
                borderTopWidth: "5px",
                borderBottomWidth: "5px",
                borderRightWidth: "0",
                borderLeftWidth: "0",
                marginTop: "3rem",
                paddingTop: "1rem",
                paddingBottom: "2rem",
              }}
            >
              <h1
                lang={titleLang === "en" ? undefined : titleLang}
                css={{ textAlign: "center", marginBottom: "0.5rem" }}
              >
                {title}
              </h1>
              <p
                css={{
                  fontFamily: "'Zilla Slab', serif",
                  fontWeight: 300,
                  fontSize: "1.4rem",
                  textAlign: "center",
                  marginTop: 0,
                }}
              >
                by {author}
              </p>
              {parse(html)}
            </main>
            <Footer data={{ title: title, slug: slug, color: color }} />
          </div>
        </div>
        <div css={{ overflow: "hidden", position: "relative" }}>
          <div
            css={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background: `linear-gradient(${
                viewportWidth < 800 ? 0 : 90
              }deg, hsl(0, 0%, 100%) 0%, hsla(0, 0%, 100%, 0.987) 2%, hsla(0, 0%, 100%, 0.951) 3.8%, hsla(0, 0%, 100%, 0.896) 5.6%, hsla(0, 0%, 100%, 0.825) 7.2%, hsla(0, 0%, 100%, 0.741) 8.8%, hsla(0, 0%, 100%, 0.648) 10.3%, hsla(0, 0%, 100%, 0.55) 11.8%, hsla(0, 0%, 100%, 0.45) 13.2%, hsla(0, 0%, 100%, 0.352) 14.7%, hsla(0, 0%, 100%, 0.259) 16.2%, hsla(0, 0%, 100%, 0.175) 17.8%, hsla(0, 0%, 100%, 0.104) 19.4%, hsla(0, 0%, 100%, 0.049) 21.1%, hsla(0, 0%, 100%, 0.013) 23%, hsla(0, 0%, 100%, 0) 25%)`,
            }}
          />
          <div
            css={{
              position: "absolute",
              top: (0.3 * viewportWidth) / (viewportWidth < 800 ? 1 : 3),
              left: (0.3 * viewportWidth) / (viewportWidth < 800 ? 1 : 3),
              width: (0.4 * viewportWidth) / (viewportWidth < 800 ? 1 : 3),
              transform:
                slug === "artist" ? "skewY(30deg) scaleX(0.9)" : undefined,
            }}
          >
            <DynamicSvg
              name={slug}
              cssObject={
                slug === "editors"
                  ? { backgroundColor: color, fill: "white" }
                  : undefined
              }
            />
          </div>
          <Canvas
            width={Math.ceil(
              viewportWidth < 800 ? viewportWidth : viewportWidth / 3
            )}
            height={Math.ceil(
              viewportWidth < 800 ? viewportWidth * 1.3 : contentHeight || 1
            )}
            params={params}
            initialPoints={initialPoints.map((point) => ({
              ...point,
              pos: {
                x:
                  (viewportWidth * (1 + point.pos.x * 0.004)) /
                  (viewportWidth < 800 ? 2 : 6),
                y:
                  (viewportWidth * (1 + point.pos.y * 0.004)) /
                  (viewportWidth < 800 ? 2 : 6),
              },
            }))}
            norms={norms}
          />
        </div>
      </div>
    </App>
  );
};

const pageQuery = graphql`
  query ($slug: String!) {
    allMarkdownRemark(sort: { order: ASC, fields: frontmatter___order }) {
      edges {
        node {
          frontmatter {
            slug
          }
        }
      }
    }
    markdownRemark(frontmatter: { slug: { eq: $slug } }) {
      frontmatter {
        slug
        title
        titleLang
        author
        color
        initialPoints {
          ang
          angChange
          bif
          bifChange
          hue
          hueChange
          lum
          lumChange
          mov {
            x
            y
          }
          opa
          opaChange
          pos {
            x
            y
          }
          rad
          radChange
          sat
          satChange
        }
        params {
          bufBottom
          bufLeft
          bufRight
          bufTop
          interval
          probSplit
          probTurn
        }
        norms {
          behavior
          range
          rangeChange
          variable
          variableChange
        }
      }
      html
    }
  }
`;

export default Post;
export { pageQuery };
